<template>
  <div
    ref="containerRef"
    class="TurnstileWidget"
    :hidden="!isVisible"
  />
</template>

<script lang="ts">
let scriptPromise: Promise<void> | null = null;

function loadTurnstileScript () {
  if (scriptPromise) return scriptPromise;

  scriptPromise = new Promise<void>((resolve, reject) => {
    if (window.turnstile) {
      resolve();
      return;
    }

    const script = document.createElement("script");
    script.src = "https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit";
    script.async = true;
    script.defer = true;
    script.onload = () => resolve();
    script.onerror = () => {
      scriptPromise = null;
      reject(new Error("Failed to load Turnstile"));
    };
    document.head.appendChild(script);
  });

  return scriptPromise;
}
</script>

<script lang="ts" setup>
import { onMounted, onUnmounted, ref, useTemplateRef } from "vue";

const token = defineModel<string>();

const emit = defineEmits<{
  error: [],
}>();

const containerRef = useTemplateRef("containerRef");

let widgetId: string | undefined;
const isVisible = ref(false);

async function render () {
  try {
    await loadTurnstileScript();
  } catch {
    emit("error");
    return;
  }

  if (!containerRef.value) return;

  widgetId = window.turnstile.render(containerRef.value, {
    sitekey: import.meta.env.VITE_TURNSTILE_SITE_KEY,
    appearance: "interaction-only",
    callback: (newToken) => {
      token.value = newToken;
    },
    "before-interactive-callback": () => {
      isVisible.value = true;
    },
    "after-interactive-callback": () => {
      isVisible.value = false;
    },
    "expired-callback": () => {
      token.value = "";
    },
    "error-callback": () => {
      token.value = "";
      emit("error");
    },
  });
}

function reset () {
  token.value = "";
  if (widgetId) window.turnstile.reset(widgetId);
}

onMounted(render);

onUnmounted(() => {
  if (widgetId) window.turnstile.remove(widgetId);
});

defineExpose({
  reset,
});
</script>

<style lang="scss" scoped>
.TurnstileWidget {
  margin-top: 12px;
}
</style>
